import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Workflow, Globe, AppWindow, Blocks } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/motion";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND, IMAGES } from "@/data/content";

const SERVICES = [
  {
    icon: Workflow,
    title: "Business Automation",
    desc: "Gmail automation, AI agents and routine-task workflows — the repetitive work handled quietly in the background while you focus on business.",
    samples: [
      { image: "/assets/sample-automation-gmail.jpg", label: "Gmail Automation — enquiries analysed by AI and answered automatically" },
      { image: "/assets/sample-automation-business.jpg", label: "AI Agent Automation — Telegram bot handling chat, calendar and email actions" },
    ],
  },
  {
    icon: Globe,
    title: "Website Development",
    desc: "Fast, elegant, SEO-friendly websites that make buyers trust you before the first email. Your digital visiting card, done properly.",
    samples: [
      { image: "/assets/sample-website-hospital.jpg", label: "Divya Jeevan Medical Hospital — appointment-ready hospital website" },
      { image: "/assets/sample-website-physio.jpg", label: "Body Harmony Physiotherapy — clinic website with online booking" },
    ],
  },
  {
    icon: AppWindow,
    title: "Web App Development",
    desc: "Custom web applications — catalogues, enquiry systems, dashboards, portals — built around how your business actually works.",
    samples: [
      { image: "/assets/sample-webapp-eximguru.jpg", label: "EXIM GURU — export incentives, HS codes & duty calculator platform" },
      { image: "/assets/sample-webapp-society.jpg", label: "Society360 — housing society management web app" },
    ],
  },
  {
    icon: Blocks,
    title: "Customise Digital Platform",
    desc: "Custom-branded chatbots and intelligent platforms built around your business — set up once, working for you every day.",
    samples: [
      { image: "/assets/sample-platform-chatbot.jpg", label: "Exim India Bot — custom-branded AI chatbot answering customer queries" },
      { image: "/assets/sample-platform-share-analysis.jpg", label: "Share Analysis Platform — multi-agent AI research delivered to your inbox" },
    ],
  },
];

export default function AIDigital() {
  usePageMeta(
    `AI Strategy & Web/App Development — ${BRAND.name}`,
    "AI strategy consulting, website and web app development for traditional businesses — from a trade veteran who understands both worlds."
  );

  const [activeService, setActiveService] = useState(null);

  return (
    <div data-testid="ai-digital-page">
      <PageHero
        kicker="Pillar 03 — Digital"
        lines={["AI Strategy &", "Digital Services."]}
        description="AI strategy, websites, web apps and digital platforms — built by someone who understands traditional trade businesses from the inside, and knows exactly how to modernise them."
        accent="teal"
        image={IMAGES.ai}
      />

      {/* SERVICES */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="kicker mb-5 text-teal">What's Offered</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              Modern Tools, <span className="italic text-teal">Honestly Applied.</span>
            </h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="h-full">
                {s.samples ? (
                  <button
                    type="button"
                    data-testid={`service-card-${i}`}
                    onClick={() => setActiveService(s)}
                    className="h-full w-full rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8 text-left transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-teal/50 hover:shadow-[0_20px_40px_-15px_rgba(0,180,216,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/60"
                  >
                    <s.icon className="h-7 w-7 text-teal" strokeWidth={1.5} />
                    <h3 className="mt-6 font-serif text-xl text-white">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-xs font-medium tracking-wide text-teal">
                      View Sample Work <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </span>
                  </button>
                ) : (
                  <div
                    data-testid={`service-card-${i}`}
                    className="h-full rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 hover:border-teal/50 hover:shadow-[0_20px_40px_-15px_rgba(0,180,216,0.25)]"
                  >
                    <s.icon className="h-7 w-7 text-teal" strokeWidth={1.5} />
                    <h3 className="mt-6 font-serif text-xl text-white">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* UNIQUE ANGLE */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(0,180,216,0.1),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <Reveal delay={0.15} className="order-2 md:order-1">
              <div className="overflow-hidden rounded-2xl border border-teal/20">
                <img src="/assets/three-pillars-banner.jpg" alt="EXIM INDIA — three pillars: export-import business, consulting & coaching, AI strategy & web/app development" className="aspect-[3/2] w-full object-cover" />
              </div>
            </Reveal>
            <Reveal className="order-1 md:order-2">
              <p className="kicker mb-5 text-teal">The Rare Combination</p>
              <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
                I Speak Three Languages: <span className="italic text-teal">Trade, Teach & Tech.</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-slate-400 md:text-base">
                Most developers have never negotiated a letter of credit. Most trade veterans have
                never shipped a line of code. And few in either world have stood before a classroom
                teaching it. I've done all three — for decades, and for real businesses with real
                money on the line.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-400 md:text-base">
                That means your digital project is designed around how trade actually works — the
                buyer's journey, the trust signals, the documents, the follow-ups — and explained
                the way a teacher would: clear, patient and jargon-free. Not a generic template —
                a tool built for your business.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <p className="kicker mb-5 text-teal">Who It's For</p>
            <h2 className="font-serif text-3xl leading-tight text-white md:text-4xl">
              For Businesses Ready to <span className="italic text-teal">Modernise.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-slate-400 md:text-base">
              Export-import firms, MSMEs, family businesses and startups who know the digital world
              is passing them by — and want a guide who respects where they've come from while
              building where they're going.
            </p>
            <div className="mt-10">
              <Link to="/contact" data-testid="ai-discuss-button" className="btn-secondary">
                Discuss Your Digital Project <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICE SAMPLE WORK MODAL */}
      <Dialog open={!!activeService} onOpenChange={(open) => !open && setActiveService(null)}>
        <DialogContent
          data-testid="service-modal"
          className="max-h-[85vh] max-w-3xl overflow-y-auto border-[#1E3A5F] bg-[#0A2540] p-0 text-white"
        >
          {activeService && (
            <div className="p-6 md:p-8">
              <p className="kicker mb-3 text-teal">Sample Work</p>
              <DialogTitle data-testid="service-modal-title" className="font-serif text-2xl text-white md:text-3xl">
                {activeService.title}
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed text-slate-400">{activeService.desc}</DialogDescription>
              <div className="mt-6 space-y-6">
                {activeService.samples?.map((sample, idx) => (
                  <figure key={sample.image}>
                    <div className="overflow-hidden rounded-xl border border-teal/20 bg-[#051320]">
                      <img
                        data-testid={`service-modal-image-${idx}`}
                        src={sample.image}
                        alt={sample.label}
                        className="w-full object-contain"
                      />
                    </div>
                    <figcaption data-testid={`service-modal-caption-${idx}`} className="mt-2 text-xs text-slate-500">
                      {sample.label}
                    </figcaption>
                  </figure>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid="service-modal-cta"
                className="btn-secondary mt-7"
                onClick={() => setActiveService(null)}
              >
                Discuss a {activeService.title} Project <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CTASection
        accent="teal"
        kicker="Build What's Next"
        title="Have a digital project in mind?"
        subtitle="Tell me where your business is today — I'll show you the fastest honest path to where it could be."
        ctaLabel="Discuss Your Digital Project"
      />
    </div>
  );
}
